import { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

import BingoGame from './components/games/BingoGame';
import ToolsPage from './pages/ToolsPage';
import {
  DEFAULT_TOOL_ID,
  TOOL_CATEGORIES,
  TOOL_DEFINITIONS,
  findToolById,
  groupToolsByCategory,
} from './modules/tools';

const getInitialDarkMode = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const storedPreference = window.localStorage.getItem('theme');
  if (storedPreference) {
    return storedPreference === 'dark';
  }

  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
};

const getInitialFavorites = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const stored = window.localStorage.getItem('favorites');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed.filter((toolId) =>
          TOOL_DEFINITIONS.some((tool) => tool.id === toolId),
        );
      }
    }
  } catch (error) {
    // Silent catch, fallback to default empty array.
  }

  return [];
};

const App = () => {
  const [darkMode, setDarkMode] = useState(getInitialDarkMode);
  const [selectedToolId, setSelectedToolId] = useState(DEFAULT_TOOL_ID);
  const [searchTerm, setSearchTerm] = useState('');
  const [favorites, setFavorites] = useState(getInitialFavorites);
  const [expandedCategories, setExpandedCategories] = useState(
    () => new Set(TOOL_CATEGORIES.map((category) => category.id)),
  );

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
      root.classList.remove('light');
      root.dataset.theme = 'dark';
    } else {
      root.classList.remove('dark');
      root.classList.add('light');
      root.dataset.theme = 'light';
    }

    window.localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    window.localStorage.setItem('favorites', JSON.stringify(favorites));
  }, [favorites]);

  const filteredTools = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) {
      return TOOL_DEFINITIONS;
    }

    return TOOL_DEFINITIONS.filter((tool) => {
      const corpus = [tool.name, tool.description, tool.subcategory]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return corpus.includes(normalized);
    });
  }, [searchTerm]);

  useEffect(() => {
    if (!filteredTools.some((tool) => tool.id === selectedToolId)) {
      setSelectedToolId(filteredTools[0]?.id ?? null);
    }
  }, [filteredTools, selectedToolId]);

  const selectedTool = findToolById(selectedToolId);
  const activeCategory = selectedTool?.category ?? null;

  const categorizedTools = useMemo(
    () => groupToolsByCategory(filteredTools),
    [filteredTools],
  );

  const handleToggleFavorite = (toolId) => {
    setFavorites((prev) => {
      const exists = prev.includes(toolId);
      if (exists) {
        return prev.filter((item) => item !== toolId);
      }
      return [...prev, toolId];
    });
  };

  const handleToggleCategory = (categoryId) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handleCategorySelect = (categoryId) => {
    const firstTool = TOOL_DEFINITIONS.find((tool) => tool.category === categoryId);
    if (firstTool) {
      setSelectedToolId(firstTool.id);
      setExpandedCategories((prev) => {
        const next = new Set(prev);
        next.add(categoryId);
        return next;
      });
    }
  };

  const handleDarkModeToggle = () => setDarkMode((prev) => !prev);

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <ToolsPage
              darkMode={darkMode}
              selectedToolId={selectedToolId}
              searchTerm={searchTerm}
              favorites={favorites}
              expandedCategories={expandedCategories}
              filteredTools={filteredTools}
              categorizedTools={categorizedTools}
              activeCategory={activeCategory}
              selectedTool={selectedTool}
              onToggleFavorite={handleToggleFavorite}
              handleCategorySelect={handleCategorySelect}
              handleToggleCategory={handleToggleCategory}
              onSearchTermChange={setSearchTerm}
              onToggleDarkMode={handleDarkModeToggle}
              setSelectedToolId={setSelectedToolId}
            />
          }
        />
        <Route path="/games/bingo" element={<BingoGame />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
